import { ROUTES } from "@via/utils/constants.tsx";
import { type OAuthConnectionSummary } from "common";
import { type JSX, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { OAuthConnectionForm } from "./oauth-connection-form.tsx";
import { remoteStorageErrorMessage } from "./remote-storage.api.tsx";
import {
  peekOAuthReturn,
  takeOAuthReturn,
  useCompleteOAuthConnection,
} from "./remote-storage-oauth.api.tsx";

/**
 * Where Google Drive or Dropbox sends the browser back (data-2-1). The code and state go to the
 * backend once, which opens the state only for the user who started the sign in. Then the save
 * step names the connection and, for Drive, picks what to browse.
 */
export default function OAuthCallback(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const complete = useCompleteOAuthConnection();
  const [datasetId] = useState(peekOAuthReturn);
  const [connection, setConnection] = useState<OAuthConnectionSummary | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const sent = useRef(false);

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const denied = searchParams.get("error");

  useEffect(() => {
    // The provider's code works once, so a second effect run (strict mode) must not send it again.
    if (sent.current) return;
    sent.current = true;
    if (denied || !code || !state) {
      setError(
        "The sign in was cancelled or did not finish. Start it again from Connect Data Source."
      );
      return;
    }
    complete
      .mutateAsync({ code, state })
      .then(setConnection)
      .catch(failure => setError(remoteStorageErrorMessage(failure)));
  }, [code, state, denied, complete]);

  const back = datasetId
    ? `${ROUTES.CREATE_DATA_SOURCE}?datasetId=${encodeURIComponent(datasetId)}`
    : ROUTES.DATA;

  const browse = (saved: OAuthConnectionSummary) => {
    takeOAuthReturn();
    const params = new URLSearchParams({ credentialId: String(saved.id) });
    if (datasetId) params.set("datasetId", datasetId);
    navigate(
      `${ROUTES.DATA_SOURCE_PROVIDER_REMOTE_STORAGE}?${params.toString()}`
    );
  };

  return (
    <div className="mx-auto max-w-[600px] p-6">
      {error ? (
        <div className="space-y-3">
          <p className="text-sm text-rose-600" role="alert">
            {error}
          </p>
          <Link className="text-sm font-semibold text-indigo-600" to={back}>
            Back to Connect Data Source
          </Link>
        </div>
      ) : connection ? (
        <>
          <h2 className="mb-4 text-lg font-semibold">Save the connection</h2>
          <OAuthConnectionForm connection={connection} onSaved={browse} />
        </>
      ) : (
        <p className="text-sm text-gray-600">Finishing the sign in</p>
      )}
    </div>
  );
}
